import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gift, Copy, Check, Share2, MessageCircle, Loader2, Users, PartyPopper, Sparkles } from "lucide-react";

export default function PatientReferrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/patient/referral")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e, "Could not load referrals")))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout role="patient"><div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div></DashboardLayout>;
  if (!data) return <DashboardLayout role="patient"><div className="p-10 text-center text-ink/50">No referral data available</div></DashboardLayout>;

  const { code, program, stats, referrals } = data;
  const shareLink = `${window.location.origin}/register?ref=${code}`;
  const rewardText = buildRewardText(program);
  const message = `Hey! I've been using EFGH to understand my lab reports better. Sign up with my code ${code} and ${rewardText.referee}. ${shareLink}`;

  const doCopy = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener");
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent("Try EFGH — smart lab reports")}&body=${encodeURIComponent(message)}`, "_blank", "noopener");

  return (
    <DashboardLayout role="patient">
      <div className="max-w-5xl space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Refer & Earn</div>
          <h1 className="mt-1 font-heading font-bold text-3xl sm:text-4xl text-ink flex items-center gap-3">
            <Gift className="w-8 h-8 text-efgh-orange" />
            Invite friends. Both win.
          </h1>
          <p className="mt-2 text-ink/60 max-w-2xl">{program?.description || "Refer a friend to EFGH. You both get rewarded."}</p>
        </div>

        {/* Hero code card */}
        <Card className="p-6 md:p-8 rounded-2xl bg-gradient-to-br from-efgh-orange/10 via-efgh-yellow/10 to-efgh-green/10 border-2 border-efgh-orange/30">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-ink/60 font-semibold">Your referral code</div>
              <div className="mt-2 font-heading font-bold text-4xl md:text-5xl text-ink tracking-wider" data-testid="referral-code">{code}</div>
              <div className="mt-3 text-sm text-ink/70">
                Friend gets <b>{rewardText.referee}</b>. You get <b>{rewardText.referrer}</b>.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button data-testid="copy-link" onClick={doCopy} className="bg-ink hover:bg-ink/90 text-white">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Copy invite link"}
              </Button>
              <Button data-testid="share-wa" onClick={shareWA} className="bg-[#25D366] hover:bg-[#25D366]/90 text-white">
                <MessageCircle className="w-4 h-4 mr-2" /> Share on WhatsApp
              </Button>
              <Button data-testid="share-email" onClick={shareEmail} variant="outline">
                <Share2 className="w-4 h-4 mr-2" /> Share via email
              </Button>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs text-ink/60">
            <Sparkles className="w-3.5 h-3.5 text-efgh-yellow" />
            <span className="truncate">{shareLink}</span>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard icon={Users} value={stats.sent} label="Invites sent" color="text-efgh-blue" bg="bg-efgh-blue/10" />
          <StatCard icon={PartyPopper} value={stats.joined} label="Friends joined" color="text-efgh-green" bg="bg-efgh-green/10" />
          <StatCard icon={Gift} value={stats.completed} label="Rewards unlocked" color="text-efgh-orange" bg="bg-efgh-orange/10" />
        </div>

        {/* Terms */}
        {program?.terms && (
          <Card className="p-4 rounded-2xl bg-muted/40">
            <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Program terms</div>
            <div className="mt-1 text-sm text-ink/70">{program.terms}</div>
          </Card>
        )}

        {/* Referrals list */}
        <div>
          <div className="mb-3">
            <h2 className="font-heading font-semibold text-xl text-ink">Your referrals</h2>
            <p className="text-sm text-ink/60">Track which friends have joined and unlocked rewards.</p>
          </div>
          <Card className="rounded-2xl">
            {(referrals || []).length === 0 ? (
              <div className="p-10 text-center text-sm text-ink/50">
                No referrals yet. Share your code above to invite your first friend!
              </div>
            ) : (
              <div className="divide-y divide-border">
                {referrals.map((r) => (
                  <div key={r.id} className="p-4 flex items-center gap-4" data-testid={`referral-row-${r.id}`}>
                    <div className="w-10 h-10 rounded-full bg-efgh-orange/10 grid place-items-center text-efgh-orange font-heading font-semibold">
                      {(r.referee_name || r.referee_email_visible || "?")[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{r.referee_name || r.referee_email_visible || "Friend"}</div>
                      <div className="text-xs text-ink/50">Joined on {r.created_at?.slice(0, 10)}</div>
                    </div>
                    <span className={`pill ${r.status === "completed" ? "pill-normal" : "pill-borderline"}`}>
                      {r.status === "completed" ? "Rewarded" : "Joined"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, value, label, color, bg }) {
  return (
    <Card className="p-4 rounded-2xl">
      <div className={`w-9 h-9 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-4 h-4" /></div>
      <div className="mt-2 font-heading font-bold text-2xl text-ink">{value || 0}</div>
      <div className="text-[11px] uppercase tracking-wider text-ink/50 mt-0.5">{label}</div>
    </Card>
  );
}

function buildRewardText(program) {
  if (!program) return { referrer: "a reward", referee: "a reward" };
  const parts = (p, f) => {
    const bits = [];
    if (p) bits.push(`${p}% off`);
    if (f) bits.push(`₹${f} off`);
    return bits.length ? bits.join(" + ") : "a reward";
  };
  return {
    referrer: parts(program.referrer_percent, program.referrer_flat),
    referee: parts(program.referee_percent, program.referee_flat),
  };
}
